"use client";

import { useState } from "react";
import { Mail, Phone, MapPin, User, MessageSquare, Loader2, Check } from "lucide-react";

import { publicService } from "@/modules/public/home/services/publicService";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contacts = [
    {
      icon: <MapPin className="w-4 h-4 text-green-600" />,
      title: "Our Address",
      lines: [
        "Way2Agribusiness India Pvt. Ltd. ಕೃಷಿ ಮಿತ್ರ , #636,",
        "BDA Block 2, APMC Yard, Yeshwanthpura,",
        "Bengaluru - 560022",
      ],
    },
    {
      icon: <Phone className="w-4 h-4 text-green-600" />,
      title: "Call Us",
      lines: ["(+91) 9449004956", "(+91) 8277078435", "(+91) 8095000388"],
    },
    {
      icon: <Mail className="w-4 h-4 text-green-600" />,
      title: "Email Us",
      lines: [
        "dr.prasannad@way2agribusiness.com",
        "way2agribusiness@gmail.com",
        "karnatakaagribusiness@gmail.com",
      ],
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.location || !formData.message) {
      setError("Please fill out all fields.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await publicService.submitContactEnquiry(formData);

      setSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        location: "",
        message: "",
      });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err: any) {
      setError(err.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
      {/* Left Column: Form */}
      <div className="lg:col-span-2 bg-white rounded-sm shadow-sm border border-gray-100 p-6 h-full flex flex-col">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-bold text-green-800  tracking-widest ml-1">
              Full Name
            </label>

            <div className="relative">
              <User className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-900" />

              <input
                type="text"
                placeholder="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full pl-9 pr-4 py-2 bg-gray-50/50 border border-gray-200 text-gray-900 text-sm rounded-sm outline-none"
                required
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-bold text-green-800  tracking-widest ml-1">
              Email Address
            </label>

            <div className="relative">
              <Mail className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-900" />

              <input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full pl-9 pr-4 py-2 bg-gray-50/50 border border-gray-200 text-gray-900 text-sm rounded-sm outline-none"
                required
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-bold text-green-800  tracking-widest ml-1">
              Phone Number
            </label>

            <div className="relative">
              <Phone className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-900" />

              <input
                type="tel"
                placeholder="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full pl-9 pr-4 py-2 bg-gray-50/50 border border-gray-200 text-gray-900 text-sm rounded-sm outline-none"
                required
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-bold text-green-800  tracking-widest ml-1">
              Location
            </label>

            <div className="relative">
              <MapPin className="absolute left-3 top-2.5 w-3.5 h-3.5  text-gray-900" />

              <input
                type="text"
                placeholder="City/Place"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full pl-9 pr-4 py-2 bg-gray-50/50 border text-gray-900 border-gray-200 text-sm rounded-sm outline-none"
                required
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5 col-span-full">
            <label className="text-sm font-bold text-green-800  tracking-widest ml-1">
              Message
            </label>

            <div className="relative h-full min-h-[120px]">
              <MessageSquare className="absolute left-3 top-3 w-3.5 h-3.5 text-gray-900" />

              <textarea
                placeholder="Your detailed requirement..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full h-full pl-9 pr-4 py-2 bg-gray-50/50 border border-gray-200 text-gray-900 text-sm rounded-sm outline-none resize-none"
                required
              />
            </div>
          </div>

          {error && (
            <div className="col-span-full text-red-600 text-xs font-bold bg-red-50 p-2.5 rounded-sm border border-red-100">
              {error}
            </div>
          )}

          {success && (
            <div className="col-span-full text-green-700 text-xs font-bold bg-green-50 p-2.5 rounded-sm border border-green-100 flex items-center gap-2">
              <Check className="w-4 h-4" /> Thank you! Your inquiry has been submitted successfully.
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="col-span-full mt-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-sm font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Submit Inquiry"
            )}
          </button>
        </form>
      </div>

      {/* Right Column: Contact Info */}
      <div className="lg:col-span-1 flex flex-col gap-3">
        {contacts.map((c, i) => (
          <div
            key={i}
            className="flex-1 p-4 bg-white border border-gray-100 rounded-sm shadow-sm hover:shadow-md transition-all flex flex-col justify-center"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-50 rounded-sm">{c.icon}</div>

              <p className="text-[11px] font-black text-green-800 uppercase tracking-[0.15em]">
                {c.title}
              </p>
            </div>

            <div className="space-y-0.5 mt-1">
              {c.lines.map((line, j) => (
                <p
                  key={j}
                  className="text-gray-900 text-xs leading-relaxed font-medium"
                >
                  {line}
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
